import React from 'react';
import {
  MapPin, BedDouble, Car, Route, Accessibility, ExternalLink,
  CalendarClock, AlarmClock, Navigation as NavIcon, Plane, Phone, Timer,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { VENUE } from '@/constants/testIds';
import { AAWTA } from '@/lib/aawta';

const openExternal = (url) => window.open(url, '_blank', 'noopener,noreferrer');

// Hotel booking deadline — end of day Sep 4, 2026 America/New_York (EDT = UTC-4)
const HOTEL_DEADLINE_ISO = '2026-09-05T03:59:59Z'; // 23:59:59 EDT on Sep 4

const daysUntil = (isoDate) => {
  const diffMs = new Date(isoDate).getTime() - Date.now();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

const CountdownRibbon = () => {
  const days = daysUntil(HOTEL_DEADLINE_ISO);
  if (days <= 0) return null;
  const label = days === 1 ? '1 day' : `${days} days`;
  return (
    <a
      href={AAWTA.hilton.bookingUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="hotel-countdown-ribbon"
      className="group block bg-gradient-to-r from-aawta-magenta via-aawta-magenta to-aawta-magenta-hover text-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-2.5 flex items-center justify-center gap-3 text-[13px] md:text-[14px] font-medium">
        <Timer className="h-4 w-4 shrink-0" />
        <span className="truncate">
          <strong>$119 preferred hotel rate</strong> expires in{' '}
          <span className="tabular-nums font-bold">{label}</span> — book by September 4, 2026
        </span>
        <span className="hidden sm:inline-flex items-center gap-1 font-semibold underline decoration-white/60 underline-offset-2 group-hover:decoration-white">
          Book now <ExternalLink className="h-3.5 w-3.5" />
        </span>
      </div>
    </a>
  );
};

const VENUE_MAP_SRC =
  'https://maps.google.com/maps?q=' +
  encodeURIComponent('Stone Ridge Event Center, 1750 Stone Ridge Drive, Stone Mountain, GA 30083') +
  '&t=m&z=14&ie=UTF8&iwloc=&output=embed';
const VENUE_DIRECTIONS_URL =
  'https://www.google.com/maps/dir/?api=1&destination=' +
  encodeURIComponent('Stone Ridge Event Center, 1750 Stone Ridge Drive, Stone Mountain, GA 30083');

const CARDS = [
  {
    icon: MapPin, title: 'Venue', body: 'Stone Ridge Event Center',
    sub: '1750 Stone Ridge Drive, Suite A · Stone Mountain, GA 30083',
    map: true,
  },
  {
    icon: BedDouble, title: 'Preferred Hotel',
    body: 'DoubleTree by Hilton Atlanta - Northlake',
    sub: '$119 Summit Rate · September 16–18, 2026 · Book by September 4',
    cta: { label: 'Book Room', url: AAWTA.hilton.bookingUrl },
  },
  {
    icon: Car, title: 'Parking', body: 'Complimentary on-site',
    sub: 'Detailed parking map shared with confirmed registrants.',
  },
  {
    icon: Route, title: 'Directions', body: 'Stone Mountain, GA',
    sub: 'Easily accessible from I-285, US-78 and Hwy-236 corridors.',
  },
  {
    icon: Accessibility, title: 'Accessibility', body: 'Fully ADA accessible',
    sub: 'Contact us for specific accommodation requests.',
  },
  {
    icon: BedDouble, title: 'Hotel Block', body: 'Preferred $119 Rate',
    sub: 'AAWTA attendees can reserve rooms through our official DoubleTree Summit hotel block through September 4, 2026, subject to availability.',
    cta: { label: 'Reserve', url: AAWTA.hilton.bookingUrl },
  },
];

export const VenueHotels = ({ onOpenInquiry }) => {
  return (
    <section id="venue" data-testid={VENUE.section} className="bg-aawta-surface">
      <CountdownRibbon />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <span className="overline text-aawta-magenta">Venue &amp; Hotels</span>
            <h2 className="font-display font-bold text-4xl md:text-5xl mt-4 text-aawta-navy tracking-tight leading-[1.1]">
              Everything you need to plan your visit.
            </h2>
            <div className="divider-magenta mt-6" />
            <p className="mt-6 text-[17px] leading-relaxed text-aawta-muted">
              The Summit is hosted at <strong className="text-aawta-navy">Stone Ridge Event Center</strong> in
              Stone Mountain, Georgia. AAWTA has also secured a preferred Summit hotel rate at{' '}
              <strong className="text-aawta-navy">DoubleTree by Hilton Atlanta - Northlake</strong>{' '}
              for attendees traveling to the event. Additional travel options, including hotels,
              flights, and rental cars, are available through our travel partner,{' '}
              <strong className="text-aawta-navy">Engine</strong>.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                data-testid={VENUE.hotelInquiryCta}
                onClick={() => openExternal(AAWTA.hilton.bookingUrl)}
                className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white font-medium px-6 py-6 shadow-aawta-magenta hover:-translate-y-0.5 transition-all"
              >
                <BedDouble className="mr-2 h-4 w-4" />
                Book Preferred Hotel
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
              <Button
                onClick={() => openExternal(AAWTA.eventbriteUrl)}
                variant="outline"
                className="rounded-full border-aawta-navy text-aawta-navy hover:bg-aawta-navy hover:text-white font-medium px-6 py-6"
              >
                Register &amp; Get Venue Info
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {CARDS.map((c, i) => {
              const Icon = c.icon;
              return (
                <div
                  key={i}
                  data-testid={`venue-card-${i + 1}`}
                  className="bg-white rounded-2xl p-6 border border-aawta-border shadow-aawta-soft hover:shadow-aawta-elevated hover:-translate-y-0.5 transition-all duration-300 flex flex-col"
                >
                  <div className="w-11 h-11 rounded-xl bg-aawta-navy text-white flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="overline mt-5 text-aawta-muted">{c.title}</div>
                  <div className="mt-1.5 text-[16px] font-semibold text-aawta-navy">
                    {c.body}
                  </div>
                  <p className="mt-1 text-sm text-aawta-muted leading-relaxed">{c.sub}</p>
                  {c.map && (
                    <div className="mt-4 rounded-xl overflow-hidden border border-aawta-border" data-testid="venue-card-map">
                      <iframe
                        title="Stone Ridge Event Center location map"
                        src={VENUE_MAP_SRC}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        className="w-full h-40 block"
                        style={{ border: 0 }}
                      />
                      <a
                        href={VENUE_DIRECTIONS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="venue-card-directions"
                        className="flex items-center justify-center gap-1.5 py-2.5 text-[12.5px] font-semibold text-aawta-navy hover:text-aawta-magenta bg-aawta-surface border-t border-aawta-border"
                      >
                        <NavIcon className="h-3.5 w-3.5" /> Get directions
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}
                  {c.cta && (
                    <button
                      type="button"
                      onClick={() => openExternal(c.cta.url)}
                      className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-aawta-magenta hover:text-aawta-magenta-hover self-start"
                      data-testid={`venue-card-cta-${i + 1}`}
                    >
                      {c.cta.label}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Stone Ridge Event Center — venue photo */}
        <div className="mt-12 rounded-2xl overflow-hidden border border-aawta-border shadow-aawta-soft relative bg-white" data-testid="venue-image">
          <img
            src={AAWTA.venueImageUrl}
            alt="Stone Ridge Event Center ballroom interior — Official venue of the 2026 AAWTA Workforce Summit"
            className="w-full h-auto max-h-[520px] object-cover block"
            loading="lazy"
          />
          <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 bg-gradient-to-t from-aawta-navy-deep/95 via-aawta-navy/70 to-transparent text-white">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-aawta-magenta">Official Venue</div>
            <div className="mt-1 font-display font-semibold text-lg md:text-xl">
              Stone Ridge Event Center — Official Home of the 2026 Workforce Summit
            </div>
          </div>
        </div>

        {/* Preferred Summit Hotel — DoubleTree feature */}
        <div className="mt-16" data-testid="preferred-hotel-block">
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <div>
              <span className="overline text-aawta-magenta">Preferred Summit Hotel</span>
              <h3 className="font-display font-bold text-3xl md:text-4xl mt-2 text-aawta-navy tracking-tight leading-tight">
                DoubleTree by Hilton Atlanta - Northlake
              </h3>
            </div>
            <div className="hidden md:block text-right">
              <div className="text-[11px] uppercase tracking-wider text-aawta-muted font-semibold">Book by</div>
              <div className="font-display font-bold text-lg text-aawta-magenta">September 4, 2026</div>
            </div>
          </div>
          <div className="divider-magenta mt-5" />

          <div className="mt-8 grid lg:grid-cols-12 gap-6 items-stretch">
            {/* Hotel photo */}
            <div className="lg:col-span-6 rounded-2xl overflow-hidden border border-aawta-border shadow-aawta-soft">
              <img
                src={AAWTA.hilton.exteriorImageUrl}
                alt="DoubleTree by Hilton Atlanta - Northlake exterior at dusk"
                className="w-full h-full min-h-[320px] max-h-[460px] object-cover block"
                loading="lazy"
                data-testid="hotel-exterior-image"
              />
            </div>

            {/* Hotel details */}
            <div className="lg:col-span-6 bg-white rounded-2xl p-6 md:p-8 border border-aawta-border shadow-aawta-soft flex flex-col">
              <div className="flex flex-wrap items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-aawta-magenta text-white flex items-center justify-center shrink-0">
                  <BedDouble className="h-5 w-5" />
                </div>
                <div>
                  <div className="overline text-aawta-muted">Hotel Address</div>
                  <div className="mt-1 font-semibold text-aawta-navy text-[15px] leading-snug">
                    4156 LaVista Road<br />Atlanta, GA 30084
                  </div>
                  <a
                    href={`tel:${AAWTA.hilton.phoneRaw}`}
                    className="mt-1.5 inline-flex items-center gap-1.5 text-[13px] text-aawta-navy hover:text-aawta-magenta"
                  >
                    <Phone className="h-3.5 w-3.5" /> {AAWTA.hilton.phone}
                  </a>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="p-4 rounded-xl bg-aawta-surface border border-aawta-border">
                  <div className="text-[10.5px] font-bold uppercase tracking-wider text-aawta-muted">Summit Rate</div>
                  <div className="mt-1 font-display font-bold text-2xl text-aawta-navy leading-none">$119<span className="text-sm font-medium text-aawta-muted"> / night</span></div>
                </div>
                <div className="p-4 rounded-xl bg-aawta-surface border border-aawta-border">
                  <div className="text-[10.5px] font-bold uppercase tracking-wider text-aawta-muted">Event Dates</div>
                  <div className="mt-1 font-display font-semibold text-[15.5px] text-aawta-navy flex items-center gap-1.5">
                    <CalendarClock className="h-4 w-4 text-aawta-magenta" /> Sep 16–18, 2026
                  </div>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between p-3.5 rounded-xl bg-white border border-aawta-border">
                  <div>
                    <div className="text-[13.5px] font-semibold text-aawta-navy">1 King Bed — Non-Smoking</div>
                    <div className="text-[12px] text-aawta-muted">Room type available for the Summit block</div>
                  </div>
                  <div className="font-display font-bold text-aawta-navy tabular-nums">$119<span className="text-[11px] font-medium text-aawta-muted">/nt</span></div>
                </div>
                <div className="flex items-center justify-between p-3.5 rounded-xl bg-white border border-aawta-border">
                  <div>
                    <div className="text-[13.5px] font-semibold text-aawta-navy">2 Queen Beds — Non-Smoking</div>
                    <div className="text-[12px] text-aawta-muted">Room type available for the Summit block</div>
                  </div>
                  <div className="font-display font-bold text-aawta-navy tabular-nums">$119<span className="text-[11px] font-medium text-aawta-muted">/nt</span></div>
                </div>
              </div>

              <div className="mt-5 flex items-start gap-3 p-4 rounded-xl bg-aawta-magenta/5 border border-aawta-magenta/20 md:hidden">
                <AlarmClock className="h-5 w-5 text-aawta-magenta shrink-0" />
                <div>
                  <div className="text-[10.5px] font-bold uppercase tracking-wider text-aawta-magenta">Reservation Deadline</div>
                  <div className="font-display font-bold text-lg text-aawta-navy">Book by September 4, 2026</div>
                </div>
              </div>

              <div className="mt-5 flex items-center gap-2 text-[13px] text-aawta-muted">
                <NavIcon className="h-4 w-4 text-aawta-magenta shrink-0" />
                <span>{AAWTA.hilton.distanceLabel}</span>
              </div>

              <div className="mt-6 pt-6 border-t border-aawta-border mt-auto">
                <Button
                  onClick={() => openExternal(AAWTA.hilton.bookingUrl)}
                  data-testid="hotel-primary-cta"
                  className="w-full rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white font-medium px-6 py-6 shadow-aawta-magenta"
                >
                  Book Your Room — $119 Summit Rate
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Engine — secondary travel resource */}
        <div className="mt-10 rounded-2xl bg-white border border-aawta-border p-6 md:p-8 shadow-aawta-soft" data-testid="engine-block">
          <div className="grid md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-1 flex md:justify-center">
              <div className="w-14 h-14 rounded-2xl bg-aawta-navy text-white flex items-center justify-center">
                <Plane className="h-6 w-6" />
              </div>
            </div>
            <div className="md:col-span-8">
              <div className="overline text-aawta-magenta">Additional Travel Options</div>
              <h4 className="font-display font-semibold text-xl md:text-2xl text-aawta-navy mt-1 leading-tight">
                Need flights, a rental car, or additional travel options?
              </h4>
              <p className="mt-2 text-[14.5px] leading-relaxed text-aawta-muted">
                AAWTA attendees can also search additional travel options through our partner{' '}
                <strong className="text-aawta-navy">Engine</strong>. When you book eligible travel
                through AAWTA&apos;s Engine partner link, your booking helps give back to support our
                education, workforce development, and career pathway initiatives.
              </p>
            </div>
            <div className="md:col-span-3 md:text-right">
              <Button
                onClick={() => openExternal(AAWTA.engineUrl)}
                data-testid="engine-cta"
                variant="outline"
                className="rounded-full border-aawta-navy text-aawta-navy hover:bg-aawta-navy hover:text-white font-medium px-6 py-6"
              >
                Search Travel with Engine
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VenueHotels;
